// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
}                                               from 'react';
import styled                                   from 'styled-components';
import { useDropzone }                          from 'react-dropzone';
import { useSelected }                          from 'slate-react';
import {
    StorageError,
    getDownloadURL,
    UploadTaskSnapshot,
    StorageReference,
}                                               from 'firebase/storage';
import ShortUniqueId                            from 'short-unique-id';
import { v4 as uuidv4 }                         from 'uuid';
// eslint-disable-next-line import/no-extraneous-dependencies
import mime                                     from 'mime';

// ===== Components =====

import { Transition }                           from '../../Transition';
import { DropzoneInput }                        from '../../../styles';
import { Button }                               from '../helpers';

// ===== Hooks =====

import { useTimeout }                           from '../../../hooks';

// ===== Services =====

import {
    uploadToCloudStorage,
    getMediaStorageBucket,
    getStorageErrorMessage,
    createMedia,
    recordUserAction,
    getPressEventHandlers,
    isTestMode,
}                                               from '../../../services';

// ===== Interfaces =====

import {
    IMediaItem,
    IPartialUserItem,
    ISnackbarItem,
}                                               from '../../../interfaces';

// ===== Enums =====
import {
    BUTTON_TYPE,
    TOOLTIP_TYPE,
    EDITOR_FIGURE_FOCUS_EVENT_TYPE,
    EDITOR_DROPZONE_SIDE_TYPE,
    MEDIA_TYPE,
    STORAGE_ENTITY,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    EDITOR_CONTEXT_TYPE,
    STORAGE_ERROR_CODE,
    USER_ACTION_TYPE,
}                                               from '../../../enums';
import { FIGURE_ORIENTATION }                   from '../elements/figureElement/enums';

// ===== Images =====

import ImageAddIcon                             from '../../../images/editor/image-add.svg';
import FileIcon                                 from '../../../images/file.svg';

// ===== Constants =====

import {
    EDITOR_FIGURE_MARGIN,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    EDITOR_FIGURE_IMAGE_CONTAINER_CLASSNAME,
    EDITOR_CAPTION_TRANSITION_DURATION,
    HOVER_TARGET_CLASSNAME,
    UPLOADING_MEDIA_DESTINATION_ID,
    BODY_FONT_SIZE,
    EXTENDED_SNACKBAR_VISIBLE_DURATION,
    SUPPORT_EMAIL_ADDRESS,
}                                               from '../../../constants/generalConstants';
import CURSOR_SIGN                              from '../../../constants/cursorSigns';

// ===== Styles =====

import { theme as themeObj }                    from '../../../themes/theme-context';

Figure.defaultProps = {
    enableGallery: false,
    parentRef: null,
    editorRef: null,
    readOnly: false,
};
interface Props {
    customCursorActive: boolean,
    currentSessionId: string | null,
    url: string,
    user: IPartialUserItem | null,
    postId: string,
    filePath: string,
    readOnly?: boolean,
    layoutType: FIGURE_ORIENTATION,
    editorType: EDITOR_CONTEXT_TYPE,
    isGalleryChild: boolean,
    rerender: boolean,
    setRerender: React.Dispatch<React.SetStateAction<boolean>>,
    createGallery: (id: string, url?: string, filePath?: string) => void,
    enableGallery?: boolean,
    parentRef?: HTMLElement | null,
    editorRef?: HTMLElement | null,
    uploadingMedia: Map<string, IMediaItem>,
    setUploadingMedia: (mediaItem: IMediaItem) => void,
    updateUploadingMedia: (mediaItem: IMediaItem) => void,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    children: React.ReactElement,
    attributes: any,
}
function Figure({
    customCursorActive,
    currentSessionId,
    url,
    user,
    postId,
    filePath,
    readOnly,
    layoutType,
    editorType,
    children,
    attributes,
    isGalleryChild,
    createGallery,
    rerender = false,
    setRerender,
    enableGallery = false,
    parentRef,
    editorRef,
    uploadingMedia,
    setUploadingMedia,
    updateUploadingMedia,
    onCursorEnter,
    onCursorLeave,
    setSnackbarData,
}: Props): JSX.Element {
    // ===== General Constants =====

    const GALLERY_MARGIN = 0.15625 * BODY_FONT_SIZE;
    const RERENDER_DELAY_DURATION = 1500; // Mainly for changing to fullScreen
    const fullscreenAnchorRef = editorRef || parentRef;

    // ===== Refs =====

    // We use this ref to access Figure ref because
    // there is an issue assigning ref directly using figure
    //
    // This likely has to do with Slate attributes
    const addImageContainerRef = useRef<HTMLDivElement>(null);

    // ===== State =====

    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [height, setHeight] = useState<number>(0);
    const [lastFocusEvent, setLastFocusEvent] = useState<EDITOR_FIGURE_FOCUS_EVENT_TYPE | null>(null);
    const [editorMarginLeft, setEditorMarginLeft] = useState<number>(0);
    const [fullscreenWidth, setFullscreenWidth] = useState<number>(0);
    const selected = useSelected();
    const [uploadingStateChangeEvent, setUploadingStateChangeEvent] = useState<{
        mediaItem: IMediaItem,
        snapshot: UploadTaskSnapshot,
        progress: number,
    } | null>(null);
    const [uploadingCompleteEvent, setUploadingCompleteEvent] = useState<{
        mediaItem: IMediaItem,
        storageRef: StorageReference,
    } | null>(null);

    // Add Image Left
    const onDropAccepted = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            handleAddImage(acceptedFiles[0]);
        }
    }, [user]);

    const {
        open: openAddLeft,
        getRootProps: getRootPropsAddLeft,
        getInputProps: getInputPropsAddLeft,
    } = useDropzone({
        noClick: true,
        noDrag: true,
        noKeyboard: true,
        accept: 'image/*',
        onDropAccepted,
    });

    const {
        open: openAddRight,
        getRootProps: getRootPropsAddRight,
        getInputProps: getInputPropsAddRight,
    } = useDropzone({
        noClick: true,
        noDrag: true,
        noKeyboard: true,
        accept: 'image/*',
        onDropAccepted,
    });

    // ===== Side Effects =====

    // Update Height
    useEffect(() => {
        if (
            addImageContainerRef.current
        ) {
            // Determine Figure Height
            let figureHeight = 0;
            const figureChildNodes = addImageContainerRef.current.parentElement?.childNodes;
            figureChildNodes?.forEach((node: ChildNode) => {
                const child = node as HTMLElement;
                if (child.classList.contains(EDITOR_FIGURE_IMAGE_CONTAINER_CLASSNAME)) {
                    figureHeight += child.clientHeight;
                } else if (child.tagName === 'FIGCAPTION') {
                    figureHeight += child.clientHeight;
                    figureHeight += parseInt(window.getComputedStyle(child).marginTop.replace('px', ''), 10);
                }
            });

            setHeight(figureHeight);

            if (rerender && setRerender) {
                setRerender(false);
            }
        }
    }, [
        layoutType,
        addImageContainerRef.current,
        rerender,
    ]);

    // Timeout for rerendering
    const {
        start: setRerenderTimeout,
        clear: clearRerenderTimeout,
    } = useTimeout(() => {
        setRerender(true);
    }, RERENDER_DELAY_DURATION);

    // Keep fullscreen left offset in sync as surrounding layout changes
    useEffect(() => {
        if (
            !fullscreenAnchorRef
            || layoutType !== FIGURE_ORIENTATION.fullscreen
        ) {
            return undefined;
        }

        const updateEditorMarginLeft = (): void => {
            const anchorRect = fullscreenAnchorRef.getBoundingClientRect();
            const parentRect = parentRef?.getBoundingClientRect();
            const marginLeft = parentRect
                ? parentRect.left - anchorRect.left
                : -anchorRect.left;
            const targetWidth = parentRect
                ? parentRect.width
                : document.documentElement.clientWidth;
            const nextMarginLeft = Math.round(marginLeft);
            const nextTargetWidth = Math.round(targetWidth);
            setEditorMarginLeft((currentMarginLeft) => (currentMarginLeft === nextMarginLeft
                ? currentMarginLeft
                : nextMarginLeft
            ));
            setFullscreenWidth((currentWidth) => (currentWidth === nextTargetWidth
                ? currentWidth
                : nextTargetWidth
            ));
        };

        let frameId: number | null = null;
        const scheduleUpdateEditorMarginLeft = (): void => {
            if (frameId != null) {
                window.cancelAnimationFrame(frameId);
            }
            frameId = window.requestAnimationFrame(() => {
                updateEditorMarginLeft();
                frameId = null;
            });
        };

        const resizeObserver = new ResizeObserver((): void => {
            scheduleUpdateEditorMarginLeft();
        });
        if (parentRef) {
            resizeObserver.observe(parentRef);
        } else {
            resizeObserver.observe(fullscreenAnchorRef);
        }

        // Sync once immediately on entry into fullscreen.
        scheduleUpdateEditorMarginLeft();

        window.addEventListener('resize', scheduleUpdateEditorMarginLeft);

        // Rerender
        clearRerenderTimeout();
        setRerenderTimeout();

        return () => {
            resizeObserver.disconnect();
            if (frameId != null) {
                window.cancelAnimationFrame(frameId);
            }
            window.removeEventListener('resize', scheduleUpdateEditorMarginLeft);
        };
    }, [
        fullscreenAnchorRef,
        parentRef,
        layoutType,
    ]);

    // Make sure permanent figure associated with
    // a transient progress item remains hidden until completed
    // transition
    //
    // Once the progress item has transitioned
    // it is popped off files.get('uploadProgress') at
    // which point this side effect runs again revealing the image
    useEffect(() => {
        if (uploadingMedia.size > 0) {
            const match = Array.from(uploadingMedia.values()).filter((uploadingItem) => {
                if (
                    uploadingItem.url === url
                    && uploadingItem.filePath === filePath
                ) {
                    return true;
                }
                return false;
            }).length > 0;
            if (!match) {
                setIsVisible(true);
            }
        } else {
            setIsVisible(true);
        }
    }, [uploadingMedia]);

    useEffect(() => {
        if (uploadingStateChangeEvent) {
            handleUploadingMediaStateChange(
                uploadingStateChangeEvent.mediaItem,
                uploadingStateChangeEvent.snapshot,
                uploadingStateChangeEvent.progress,
            );
            // setUploadingStateChangeEvent(null);
        }
    }, [uploadingStateChangeEvent]);

    useEffect(() => {
        if (uploadingCompleteEvent) {
            // Handle successful uploads on complete
            getDownloadURL(uploadingCompleteEvent.storageRef).then((downloadURL: string) => {
                handleUploadingMediaComplete(uploadingCompleteEvent.mediaItem, downloadURL);
            });
        }
    }, [uploadingCompleteEvent]);

    // ===== Methods =====

    const handleFocus = (event: EDITOR_FIGURE_FOCUS_EVENT_TYPE): void => {
        if (
            event === EDITOR_FIGURE_FOCUS_EVENT_TYPE.click
            && lastFocusEvent === EDITOR_FIGURE_FOCUS_EVENT_TYPE.click
            && selected
        ) {
            // Case # 1
            // Last event was click and it focused figure
            // We wish to make unlocked
            setLastFocusEvent(null);
        } else if (
            event === EDITOR_FIGURE_FOCUS_EVENT_TYPE.click
            && !lastFocusEvent
        ) {
            // Case # 2
            // Last event was null, which only occurs
            // if we hit Case #1 after hitting Case #3
            // We wish to close it
            // setIsFocused(true);
            setLastFocusEvent(EDITOR_FIGURE_FOCUS_EVENT_TYPE.click);
        } else if (
            event === EDITOR_FIGURE_FOCUS_EVENT_TYPE.click
            && lastFocusEvent === EDITOR_FIGURE_FOCUS_EVENT_TYPE.mouseEnter
            && selected
        ) {
            // Case # 3
            // Last event was mouse enter and it focused figure
            // We wish to lock it focused
            // setIsFocused(true);
            setLastFocusEvent(EDITOR_FIGURE_FOCUS_EVENT_TYPE.click);
        } else if (
            event === EDITOR_FIGURE_FOCUS_EVENT_TYPE.mouseEnter
            && lastFocusEvent !== EDITOR_FIGURE_FOCUS_EVENT_TYPE.click
        ) {
            // Case # 4
            // Last event was mouse leave (Case #5)
            // We wish to temporarily make focused
            // setIsFocused(true);
            setLastFocusEvent(EDITOR_FIGURE_FOCUS_EVENT_TYPE.mouseEnter);
        } else if (
            event === EDITOR_FIGURE_FOCUS_EVENT_TYPE.mouseLeave
            && lastFocusEvent !== EDITOR_FIGURE_FOCUS_EVENT_TYPE.click
        ) {
            // Case # 5
            // Last event was a mouse enter
            // We wish to remove temporary focus
            // setIsFocused(false);
            setLastFocusEvent(EDITOR_FIGURE_FOCUS_EVENT_TYPE.mouseLeave);
        }
    };

    const handleAddImage = (file: File): void => {
        if (!user) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem adding image. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            throw Error(`There was a problem adding image. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }

        const mediaId = uuidv4();
        const uniqueId = new ShortUniqueId({ length: 6 })(); // avoid file name collisions
        const mediaBucket = getMediaStorageBucket(MEDIA_TYPE.image);
        const fileName = file.name.toLowerCase().split('.')[0].replace(' ', '_');
        const fileExtension = mime.getExtension(file.type);
        if (fileExtension == null) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: `Unable to determine file extension. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                icon: FileIcon,
                hasFailure: true,
            });
            return;
        }
        let storageEntity: STORAGE_ENTITY;
        if (editorType === EDITOR_CONTEXT_TYPE.post) {
            storageEntity = isTestMode()
                ? STORAGE_ENTITY.stagingPosts
                : STORAGE_ENTITY.posts;
        } else {
            storageEntity = isTestMode()
                ? STORAGE_ENTITY.stagingAnnotations
                : STORAGE_ENTITY.annotations;
        }
        const path = `${storageEntity}/${mediaBucket}/${mediaId}/${fileName}-${uniqueId}.${fileExtension}`;
        const mediaItem: IMediaItem = {
            id: mediaId,
            userId: user.id,
            file,
            filePath: path,
            type: MEDIA_TYPE.image,
            uploadProgress: 0,
            caption: '',
        };
        setUploadingMedia(mediaItem);

        // make db entry of media
        // execute before file upload so upload cloud function has a place to write to
        createMedia({
            test: isTestMode(),
            postId,
            mediaId,
            type: MEDIA_TYPE.image,
            storageEntity,
            fileName: `${fileName}-${uniqueId}`,
            fileExtension: fileExtension.toString(),
            caption: '',
        });

        // upload file to cloud storage
        // url will be set by cloud function
        const uploadTask = uploadToCloudStorage(
            file,
            path,
            user.id,
        );

        uploadTask.on(
            'state_changed',
            (snapshot: UploadTaskSnapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadingStateChangeEvent({
                    mediaItem,
                    snapshot,
                    progress,
                });
            },
            (error: StorageError) => {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                        },
                    });
                }
                throw Error(getStorageErrorMessage(error.code as STORAGE_ERROR_CODE));
            },
            () => {
                setUploadingCompleteEvent({
                    mediaItem,
                    storageRef: uploadTask.snapshot.ref,
                });
            },
        );
    };

    const onButtonMouseEnter = (e: React.MouseEvent): void => {
        if (onCursorEnter) {
            onCursorEnter(
                CURSOR_TARGET.editorElementButton,
                [CURSOR_SIGN.click],
                e.currentTarget as HTMLElement,
            );
        }
    };

    const onButtonMouseLeave = (e: React.MouseEvent): void => {
        if (onCursorLeave) onCursorLeave(e);
    };

    const handleUploadingMediaComplete = (
        mediaItem: IMediaItem,
        imgURL: string,
    ): void => {
        // Update Uploading Media
        updateUploadingMedia({
            ...uploadingMedia.get(mediaItem.id)!,
            url: imgURL,
        });

        // No need to update DB because filePath remains the same

        // Create Gallery
        createGallery(mediaItem.id, undefined, mediaItem.filePath);
    };

    const handleUploadingMediaStateChange = (
        mediaItem: IMediaItem,
        _snapshot: UploadTaskSnapshot,
        progress: number,
    ): void => {
        if (uploadingMedia.has(mediaItem.id)) {
            updateUploadingMedia({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ...uploadingMedia.get(mediaItem.id)!,
                uploadProgress: progress,
            });
        } else {
            setUploadingMedia(mediaItem);
        }
    };

    return (
        <Container
            {...attributes}
            id={UPLOADING_MEDIA_DESTINATION_ID(filePath)}
            isVisible={isVisible}
            layoutType={layoutType}
            isGalleryChild={isGalleryChild}
            editorMarginLeft={editorMarginLeft}
            fullscreenWidth={fullscreenWidth}
            galleryMargin={GALLERY_MARGIN}
            {...getPressEventHandlers({ onMouseDown: () => handleFocus(EDITOR_FIGURE_FOCUS_EVENT_TYPE.click) })}
        >
            {(
                layoutType === FIGURE_ORIENTATION.left
                || layoutType === FIGURE_ORIENTATION.right
            ) && !readOnly
            && (
                <FloatPaddingContainer
                    height={height}
                />
            )}
            {children}
            <Transition
                in={
                    enableGallery
                    && !readOnly
                    && selected
                    && (
                        layoutType === FIGURE_ORIENTATION.right
                        || layoutType === FIGURE_ORIENTATION.center
                    )
                    && !isGalleryChild
                }
                timeout={{
                    enter: EDITOR_CAPTION_TRANSITION_DURATION,
                    exit: EDITOR_CAPTION_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(transitionState) => (
                    <AddImageContainer
                        {...getRootPropsAddLeft({
                            ref: addImageContainerRef,
                            className: HOVER_TARGET_CLASSNAME,
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                            onClick: (e) => {
                                e.stopPropagation();
                                openAddLeft();
                            },
                            side: EDITOR_DROPZONE_SIDE_TYPE.left,
                            style: {
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: 'up',
                                    offset: 10,
                                    duration: EDITOR_CAPTION_TRANSITION_DURATION,
                                    easing: themeObj.motion.eagerEasing,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: 'up',
                                    offset: 10,
                                })[transitionState],
                            },
                        })}
                    >
                        <DropzoneInput {...getInputPropsAddLeft()} />
                        <Button
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.secret}
                            height={35}
                            icon={ImageAddIcon}
                            tooltip={{
                                active: true,
                                text: 'Add Image',
                                side: TOOLTIP_TYPE.top,
                            }}
                        />
                    </AddImageContainer>
                )}
            </Transition>
            <Transition
                in={
                    enableGallery
                    && !readOnly
                    && selected
                    && (
                        layoutType === FIGURE_ORIENTATION.left
                        || layoutType === FIGURE_ORIENTATION.center
                    )
                    && !isGalleryChild
                }
                timeout={{
                    enter: EDITOR_CAPTION_TRANSITION_DURATION,
                    exit: EDITOR_CAPTION_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(transitionState) => (
                    <AddImageContainer
                        {...getRootPropsAddRight({
                            className: HOVER_TARGET_CLASSNAME,
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                            onClick: (e) => {
                                e.stopPropagation();
                                openAddRight();
                            },
                            side: EDITOR_DROPZONE_SIDE_TYPE.right,
                            style: {
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: 'up',
                                    offset: 10,
                                    duration: EDITOR_CAPTION_TRANSITION_DURATION,
                                    easing: themeObj.motion.eagerEasing,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: 'up',
                                    offset: 10,
                                })[transitionState],
                            },
                        })}
                    >
                        <DropzoneInput {...getInputPropsAddRight()} />
                        <Button
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.secret}
                            height={35}
                            icon={ImageAddIcon}
                            tooltip={{
                                active: true,
                                text: 'Add Image',
                                side: TOOLTIP_TYPE.top,
                            }}
                        />
                    </AddImageContainer>
                )}
            </Transition>
        </Container>
    );
}

// ===== Styled Components =====

interface ContainerProps {
    isVisible: boolean,
    layoutType: FIGURE_ORIENTATION,
    editorMarginLeft: number,
    fullscreenWidth: number,
    isGalleryChild: boolean,
    galleryMargin: number,
}
const Container = styled.figure<ContainerProps>`
    position: relative;
    // Rather than be none, we want a transparent the background color
    // so that the background transitions in response to hover events
    background: transparent;
    left: ${({
        layoutType,
        editorMarginLeft,
    }) => (layoutType === FIGURE_ORIENTATION.fullscreen
        ? `${editorMarginLeft}px`
        : 'auto'
    )};
    width: ${({
        layoutType,
        fullscreenWidth,
        isGalleryChild,
        galleryMargin,
    }) => {
        if (layoutType === FIGURE_ORIENTATION.fullscreen) {
            return fullscreenWidth > 0 ? `${fullscreenWidth}px` : '100vw';
        }

        if (
            (
                layoutType === FIGURE_ORIENTATION.left
                || layoutType === FIGURE_ORIENTATION.right
            )
            || isGalleryChild
        ) {
            return `calc(50% - ${galleryMargin}px)`;
        }
        return '90%';
    }};
    float: ${({ layoutType }) => {
        if (layoutType === FIGURE_ORIENTATION.left) {
            return 'left';
        }

        if (layoutType === FIGURE_ORIENTATION.right) {
            return 'right';
        }
        return 'none';
    }};
    margin: ${({ layoutType }) => (layoutType === FIGURE_ORIENTATION.center
        ? `${EDITOR_FIGURE_MARGIN}px auto`
        : `${EDITOR_FIGURE_MARGIN}px 0px`
    )};
    margin-right: ${({ layoutType, isGalleryChild, galleryMargin }) => {
        if (layoutType === FIGURE_ORIENTATION.fullscreen) {
            return '0px';
        }

        if (
            layoutType === FIGURE_ORIENTATION.left
            && !isGalleryChild
        ) {
            return `${1.25 * BODY_FONT_SIZE}px`;
        }

        if (
            layoutType === FIGURE_ORIENTATION.left
            && isGalleryChild
        ) {
            return `${galleryMargin}px`;
        }
        return 'auto';
    }};
    margin-left: ${({ layoutType, isGalleryChild }) => {
        if (layoutType === FIGURE_ORIENTATION.fullscreen) {
            return '0px';
        }

        if (layoutType === FIGURE_ORIENTATION.right && !isGalleryChild) {
            return `${1.25 * BODY_FONT_SIZE}px`;
        }

        if (layoutType === FIGURE_ORIENTATION.right && isGalleryChild) {
            return `${0.15625 * BODY_FONT_SIZE}px`;
        }
        return 'auto';
    }};
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
    transition: ${({ theme }) => `
        opacity 300ms ${theme.motion.standardEasing}
    `};
`;

interface AddImageContainerProps {
    side: EDITOR_DROPZONE_SIDE_TYPE,
}
const AddImageContainer = styled.div<AddImageContainerProps>`
    position: absolute;
    top: ${`${0.5 * BODY_FONT_SIZE}px`};
    left: ${({ side }) => (side === EDITOR_DROPZONE_SIDE_TYPE.left
        ? `-${2.8125 * BODY_FONT_SIZE}px`
        : 'auto'
    )};
    right: ${({ side }) => (side === EDITOR_DROPZONE_SIDE_TYPE.right
        ? `-${2.8125 * BODY_FONT_SIZE}px`
        : 'auto'
    )};
`;

interface FloatPaddingContainerProps {
    height: number,
}
const FloatPaddingContainer = styled.div<FloatPaddingContainerProps>`
    padding-bottom: ${({ height }) => `${height}px`};
`;

export default Figure;
